import React, { useState } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, LoginModal } from '../common';
import CartItem from './CartItem';
import { useCart } from '../../hooks/useCart';
import { useAuthStore } from '../../store/useAuthStore';
import { formatCurrency } from '../../utils/format';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { items, subtotal, total, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isAuthenticated } = useAuthStore();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleCheckoutClick = () => {
    if (!isAuthenticated) {
      // Keep cart open, show login modal on top
      setShowLoginModal(true);
    } else {
      onClose();
      navigate('/checkout');
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-dark-light dark:bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } transition-colors duration-300`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-dark dark:border-gray-100 transition-colors duration-300">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-accent dark:text-primary transition-colors duration-300" />
              <h2 className="text-xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
                Your Cart
              </h2>
              {items.length > 0 && (
                <span className="bg-accent dark:bg-primary text-white text-sm px-2 py-0.5 rounded-full transition-colors duration-300">
                  {items.length}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary hover:bg-dark dark:hover:bg-gray-100 rounded-lg transition-colors duration-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <ShoppingBag className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4 transition-colors duration-300" />
                <h3 className="text-lg font-semibold text-gray-300 dark:text-gray-600 mb-2 transition-colors duration-300">
                  Your cart is empty
                </h3>
                <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
                  Add some fresh products to get started!
                </p>
                <Button variant="primary" onClick={() => { onClose(); }}>
                  <Link to="/products">Browse Products</Link>
                </Button>
              </div>
            ) : (
              <>
                {items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeFromCart}
                  />
                ))}
                
                {/* Clear Cart Button */}
                <button
                  onClick={clearCart}
                  className="w-full text-center text-sm text-red-500 hover:text-red-600 py-2 transition-colors duration-300"
                >
                  Clear Cart
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-dark dark:border-gray-100 p-4 space-y-4 transition-colors duration-300">
              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">Total</span>
                <span className="text-2xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
                  {formatCurrency(total)}
                </span>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <Button variant="primary" size="lg" className="w-full" onClick={handleCheckoutClick}>
                  Checkout Now
                </Button>
                <Button variant="ghost" size="lg" className="w-full" onClick={onClose}>
                  Continue Shopping
                </Button>
              </div>

              {/* Login Modal */}
              <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                redirectToCheckout
              />

              {/* Free Shipping Notice */}
              {subtotal < 50 && (
                <p className="text-center text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  Add {formatCurrency(50 - subtotal)} more for FREE shipping!
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartDrawer;